import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { INotification } from '@app/modules/notifications/interface/notification';
import { NOTIFICATION_TYPE } from '@app/modules/notifications/conts/notification';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { NotificationsState } from '@app/modules/notifications/store/notifications.state';
import { NotificationsActions } from '@app/modules/notifications/store/notifications.actions';
import { filter } from 'rxjs/operators';
import { InvitationsState } from '@app/modules/invitations/store/invitations.state';
import { IAllInvitation } from '@app/modules/invitations/interface/invitation';
import { UserState } from '@app/modules/user/store/user.state';
import { USER_ROLE } from '@app/modules/user/consts/role';

@Component({
  selector: 'app-notification-sidebar',
  templateUrl: './notification-sidebar.component.html',
  styleUrls: ['./notification-sidebar.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationSidebarComponent implements OnInit, OnDestroy {
  @Select(NotificationsState.sidebarVisible)
  public sidebarVisible$: Observable<boolean>;

  @Select(NotificationsState.newNotificationCount)
  public newNotificationCount$: Observable<number>;

  @Select(NotificationsState.notificationCountByType)
  public notificationCountByType$: Observable<{
    livingWillUpdated: number;
    generalInfoUpdated: number;
    criticalInfoUpdated: number;
  }>;

  @Select(NotificationsState.allNotifications)
  public allNotifications$: Observable<INotification[]>;

  @Select(NotificationsState.newNotifications)
  public newNotifications$: Observable<INotification[]>;

  @Select(NotificationsState.viewedNotifications)
  public viewedNotifications$: Observable<INotification[]>;

  @Select(InvitationsState.allInvitations)
  public allInvitatios$: Observable<IAllInvitation[]>;

  @Select(InvitationsState.newInvitations)
  public newInvitatios$: Observable<IAllInvitation[]>;

  @Select(InvitationsState.newInvitationsCount)
  public newInvitationsCount$: Observable<Number>;

  @Select(UserState.userRole)
  public userRole$: Observable<USER_ROLE>;

  public loading = false;

  public activeTab$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  private _subscription = new Subscription();

  /**
   * @param store
   * @param actions
   * @param cdr
   */
  constructor(
    private readonly store: Store,
    private readonly actions: Actions,
    private readonly cdr: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this._subscription.add(
      this.actions
        .pipe(
          ofActionDispatched(
            NotificationsActions.GetNotificationsSuccess,
            NotificationsActions.GetNotificationsFailed
          )
        )
        .subscribe(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
    );

    this._subscription.add(
      this.sidebarVisible$.subscribe((visible: boolean) => {
        if (visible) {
          this.activeTab$.next(0);
        }
        this.cdr.detectChanges();
      })
    );
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  public markAsDone(newNotifications: INotification[]): void {
    this.loading = true;
    if (this.activeTab$.value === 0) {
      this.store.dispatch(new NotificationsActions.MarkAsReadAll());
    } else {
      const notificationIds = newNotifications.map((x) => x.id);
      this.store.dispatch(new NotificationsActions.MarkAsRead(notificationIds));
    }
  }

  public changeNotificationSidebarVisible(value: boolean): void {
    const sidebarVisible = this.store.selectSnapshot(
      NotificationsState.sidebarVisible
    );

    if (value !== sidebarVisible) {
      this.store.dispatch(new NotificationsActions.ChangeSidebarVisible(value));
      this.cdr.detectChanges();
    }
  }

  public onTabChange(val): void {
    let notificationType = [];

    switch (val.index) {
      case 0: // All
        break;
      case 1: // Critical
        notificationType = [NOTIFICATION_TYPE.CriticalInfoUpdated];
        break;
      case 2: // Invitations
        notificationType = [NOTIFICATION_TYPE.InvitationStatusUpdated];
        break;
      case 3: // General
        notificationType = [
          NOTIFICATION_TYPE.GeneralInfoUpdated,
          NOTIFICATION_TYPE.LivingWillUpdated,
        ];
        break;
    }

    this.activeTab$.next(val.index);
    this.loading = true;
    this.store.dispatch(
      new NotificationsActions.GetNotifications(1, notificationType)
    );
  }

  public onScroll(): void {
    const currentPage = this.store.selectSnapshot(
      NotificationsState.currentPage
    );
    const totalPages = this.store.selectSnapshot(NotificationsState.totalPages);

    if (currentPage < totalPages) {
      this.store.dispatch(
        new NotificationsActions.GetNotifications(currentPage + 1)
      );
    }
  }
}
